import React, { useState } from 'react';
import { Button } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { FilterBar, useFilterBarStyles } from '../ui';
import { dateParam } from '../../lib/orderFilters';

interface Props {
    onFilter: (params: { createdAt_min: string, createdAt_max: string }) => void;
    loading?: boolean;
    defaultParams?: any;
}

const getToday = () => dayjs().format('YYYY-MM-DD');

/** Date range of the report; edits are applied with "Apply", "Reset" goes back to today. */
const SessionConversionFilters: React.FC<Props> = ({ onFilter, loading, defaultParams }) => {
    const classes = useFilterBarStyles();
    const [createdAt_min, setMin] = useState(defaultParams?.createdAt_min || getToday());
    const [createdAt_max, setMax] = useState(defaultParams?.createdAt_max || getToday());
    const [error, setError] = useState('');

    const handleApply = () => {
        const minDate = dayjs(createdAt_min);
        const maxDate = dayjs(createdAt_max);
        const diff = maxDate.diff(minDate, 'day');
        if (diff < 0) {
            setError('The end date must not be before the start date');
            return;
        }
        if (diff > 30) {
            setError('Maximum range is 30 days');
            return;
        }
        setError('');
        onFilter({ createdAt_min, createdAt_max });
    };

    const handleClear = () => {
        const today = getToday();
        setMin(today);
        setMax(today);
        setError('');
        onFilter({ createdAt_min: today, createdAt_max: today });
    };

    return (
        <FilterBar label="Report date range">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                    className={classes.date}
                    label="From"
                    format="YYYY-MM-DD"
                    value={dayjs(createdAt_min)}
                    maxDate={dayjs(createdAt_max)}
                    onChange={(value) => { const iso = dateParam(value); if (iso) setMin(iso); }}
                    disabled={loading}
                    slotProps={{ textField: { size: 'small' } }}
                />
                <DatePicker
                    className={classes.date}
                    label="To"
                    format="YYYY-MM-DD"
                    value={dayjs(createdAt_max)}
                    minDate={dayjs(createdAt_min)}
                    maxDate={dayjs()}
                    onChange={(value) => { const iso = dateParam(value); if (iso) setMax(iso); }}
                    disabled={loading}
                    slotProps={{ textField: { size: 'small' } }}
                />
            </LocalizationProvider>
            <Button variant="contained" size="small" onClick={handleApply} disabled={loading}>
                Apply
            </Button>
            <Button className={classes.clear} variant="text" size="small" onClick={handleClear} disabled={loading}>
                Reset to today
            </Button>
            {error && <p className={classes.hint} role="alert">{error}</p>}
        </FilterBar>
    );
};

export default SessionConversionFilters;
